import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCurrentUserQuery } from '@/api/user/index.ts';
import { createSwapMarginConverter } from '@/App/tiles/shared/CommercialMargin/converters.ts';
import { MarginAmountDisplay } from '@/App/tiles/shared/CommercialMargin/MarginAmountDisplay.tsx';
import { MarginSection, type MarginSectionProps } from '@/App/tiles/shared/CommercialMargin/MarginSection.tsx';
import { useCheckSwapByTradeCapture } from '@/App/tiles/Swap/SwapContext.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectSwapPrices } from '@/store/swap/selectors/rfs.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import {
  swapCommercialMarginCurrencyToggled,
  swapCommercialMarginSpotPointsUpdated,
  swapCommercialMarginSwapPointsUpdated,
} from '@/store/swap/slice.ts';
import type { BidAskPoint } from '@/utils/number-point.ts';
import { convertBidAskRateToBidAskPoints } from '@/utils/number-point.ts';

export type SwapCommercialMarginProps = {
  /** Unique identifier for the swap tile */
  tileId: string;

  /** Controls the visibility of the commercial margin section */
  isCommercialMarginVisible: boolean;

  /** Callback to toggle the commercial margin visibility */
  onCommercialMarginVisibilityToggle: () => void;
};

/**
 * Commercial margin component for Swap tiles
 * Displays and allows editing of commercial margin spot and swap points
 * Only visible for Internal Sales users
 */
export const SwapCommercialMargin = (props: SwapCommercialMarginProps) => {
  const { tileId, isCommercialMarginVisible, onCommercialMarginVisibilityToggle } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { formatMessage } = useIntl();

  const dispatch = useAppDispatch();

  const checkSwapWithTradeCapture = useCheckSwapByTradeCapture();

  const { data: user } = useCurrentUserQuery();

  const isUneven = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);

    // Only return true if tile is defined, not non-deliverable, and type is 'uneven'
    return isDefined(tile) && tile.isNonDeliverable === false && tile.properties.type === 'uneven';
  });

  const { prices, marginAmountCurrency = '' } = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);
    if (isNotDefined(tile)) {
      return {};
    }

    // default index is 2, like `generateSwapTile` (it is reset to undefined when a tile is duplicated)
    const currencyIndex = tile.properties.commercialMarginAmountCurrencyIndex ?? 2;

    return {
      prices: selectSwapPrices(state, tileId),
      marginAmountCurrency: (currencyIndex === 1 ? tile.properties.currency1 : tile.properties.currency2) ?? '',
    };
  }, deepEqual);

  const isEditable = isDefined(prices);

  const updateMarginSpotPoints = useCallback(
    (value: BidAskPoint) => {
      sgwtWebAnalytics?.trackEvent(
        'Swap',
        'Commercial margin spot points changed',
        isDefined(value) ? `${value.bid}/${value.ask}` : '',
      );

      dispatch(
        swapCommercialMarginSpotPointsUpdated({
          tileId,
          value,
        }),
      );

      if (isDefined(prices?.pinnedNearPrice)) {
        checkSwapWithTradeCapture({
          nearPrice: null,
        });
      }
    },
    [checkSwapWithTradeCapture, tileId, prices?.pinnedNearPrice, sgwtWebAnalytics],
  );

  const handleResetSpotMargin = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Commercial margin spot points reseted');

    dispatch(
      swapCommercialMarginSpotPointsUpdated({
        tileId,
        value: undefined,
      }),
    );
  }, [tileId, sgwtWebAnalytics]);

  const handleUpdateSwapMargin = useCallback(
    (value: BidAskPoint) => {
      sgwtWebAnalytics?.trackEvent('Swap', 'Commercial margin swap points changed', `${value.bid}/${value.ask}`);

      dispatch(
        swapCommercialMarginSwapPointsUpdated({
          tileId,
          value,
        }),
      );
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleResetSwapMargin = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Commercial margin swap points reseted');

    dispatch(
      swapCommercialMarginSwapPointsUpdated({
        tileId,
        value: undefined,
      }),
    );
  }, [tileId, sgwtWebAnalytics]);

  const handleToggleCurrency = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Commercial margin currency toggled');
    dispatch(swapCommercialMarginCurrencyToggled({ tileId }));
  }, [tileId, sgwtWebAnalytics]);

  const commercialMarginSpotPoints = useMemo(() => {
    if (isNotDefined(prices)) {
      return { bid: 0, ask: 0 };
    }

    return convertBidAskRateToBidAskPoints(prices.appliedMarginSpotDeltaRate, prices.currencyPairPrecision);
  }, [prices]);

  const sections: MarginSectionProps[] = useMemo(() => {
    const sectionsList: MarginSectionProps[] = [];

    if (isUneven) {
      sectionsList.push({
        type: 'spot',
        label: formatMessage({ id: 'SwapTile.commercialMargin.spotPps' }),
        value: commercialMarginSpotPoints,
        isEditable,
        increment: 1,
        converter: createSwapMarginConverter(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        dataE2ePrefix: 'swap-commercial-margin-spot-pps',
        onUpdate: updateMarginSpotPoints,
        onReset: handleResetSpotMargin,
      });
    }

    sectionsList.push({
      type: 'swap',
      label: formatMessage({ id: 'SwapTile.commercialMargin.swapPps' }),
      value: prices?.commercialMarginSwapPoints ?? prices?.commercialMarginDefaultSwapPoints ?? { bid: 0, ask: 0 },
      defaultValue: prices?.commercialMarginDefaultSwapPoints ?? { bid: 0, ask: 0 },
      isEditable,
      increment: 0.1,
      converter: createSwapMarginConverter(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      dataE2ePrefix: 'swap-commercial-margin-swap-pps',
      onUpdate: handleUpdateSwapMargin,
      onReset: handleResetSwapMargin,
    });

    return sectionsList;
  }, [
    prices,
    isEditable,
    isUneven,
    commercialMarginSpotPoints,
    updateMarginSpotPoints,
    handleResetSpotMargin,
    handleUpdateSwapMargin,
    handleResetSwapMargin,
    formatMessage,
  ]);

  if (isNotDefined(user?.userType) || user.userType !== 'Internal Sales') {
    return null;
  }

  const { commercialMarginAmount = { bid: 0, ask: 0 }, commercialMarginAmountCurrency } = prices ?? {};

  return (
    <>
      <HStack verticalAlignment="center" fullWidth className="px-2">
        <div className="flex-fill border-top" style={{ height: 1 }} />

        <Button
          size="sm"
          purpose="flat"
          onClick={onCommercialMarginVisibilityToggle}
          data-e2e="swap-commercial-margin-toggle-button"
        >
          <FormattedMessage id="SwapTile.commercialMargin.button" />
          <i className="icon icon-sm">{isCommercialMarginVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
        </Button>

        <div className="flex-fill border-top" style={{ height: 1 }} />
      </HStack>

      {isCommercialMarginVisible && (
        <div
          className="px-2 pt-4px pb-12px"
          style={{
            display: 'grid',
            gap: '5px',
            gridTemplateColumns: '100px 131px 40px',
            gridTemplateRows: '20px',
          }}
        >
          {sections.map((section) => (
            <MarginSection key={section.type} {...section} />
          ))}

          <MarginAmountDisplay
            amount={commercialMarginAmount}
            currency={commercialMarginAmountCurrency ?? marginAmountCurrency}
            isEditable={isEditable}
            onCurrencyToggle={handleToggleCurrency}
            dataE2ePrefix="swap-commercial-margin"
          />
        </div>
      )}
    </>
  );
};
